import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { Validators, Editor, Toolbar } from "ngx-editor";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { NgxEditorModule } from "ngx-editor";
import { DEFAULT_COLOR_PRESETS, DEFAULT_EDITOR_TOOLBAR } from "./xhunt-edtior.constant";
import { tap } from "rxjs";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";

@Component({
  selector: "app-xhunt-editor",
  standalone: true,
  imports: [
    CommonModule,
    NgxEditorModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: "./xhunt-editor.component.html",
  styleUrl: "./xhunt-editor.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XhuntEditorComponent {
  editor: Editor;
  #destroyRef = inject(DestroyRef);
  reactiveForm: FormGroup = new FormGroup({
    editorControl: new FormControl<string>("", [Validators.required()]),
  });
  @Input() toolbar: Toolbar = DEFAULT_EDITOR_TOOLBAR;
  @Input() set data(data: string) {
    this.reactiveForm.get("editorControl")?.setValue(data);
  }
  @Input() showToolBar = true;
  @Input() showMenu = true;
  @Input() set readonlyControl(value: boolean) {
    if (value) {
      this.reactiveForm.get("editorControl")?.disable();
    }
  }
  @Output() editorChange = new EventEmitter<string>();
  @ViewChild('fileInput') fileInput: ElementRef;
  @ViewChild('editorInput', { read: ElementRef }) editorInput: ElementRef;
  @Input() placeholder = '';

  colorPresets = DEFAULT_COLOR_PRESETS

  constructor() {
    this.editor = new Editor({
      keyboardShortcuts: true,
      history: true,
    });
  }

  ngOnInit(): void {
    this.reactiveForm.valueChanges
      .pipe(
        tap((v) => {
          this.editorChange.emit(this.reactiveForm.get("editorControl")?.value);
        })
      )
      .subscribe();
    this.#destroyRef.onDestroy(() => this.editor.destroy());
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.convertFileToBase64(file);
      this.clearFileInput();
    }
  }

  clearFileInput(): void {
    this.fileInput.nativeElement.value = '';
  }

  convertFileToBase64(file: File): void {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      let image = `<img src='${reader.result}'  alt="${file.name}"/>`;
      this.reactiveForm.setValue({
        editorControl: this.reactiveForm.value.editorControl + image
      })
    };
    reader.onerror = (error) => {
    };
  }

  toPlaintText() {
    const currentContent = this.reactiveForm.get("editorControl")?.value;
    this.reactiveForm.patchValue({
      editorControl: this.convertHeadingsToDivsAndRemoveStyling(currentContent)
    });
  }
  convertHeadingsToDivsAndRemoveStyling(htmlString) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    const headingElements = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');

    headingElements.forEach(heading => {
      const div = document.createElement('div');
      div.textContent = heading?.textContent;
      div.className = heading.className;
      div.id = heading.id;

      heading.parentNode.replaceChild(div, heading);
    });

    doc.querySelectorAll('*').forEach(element => {
      element.removeAttribute('style');
    });

    const modifiedHtml = doc.documentElement.outerHTML;

    return modifiedHtml;
  }

  focus() {
    // Try to focus the underlying ngx-editor contenteditable
    setTimeout(() => {
      const el: HTMLElement | null =
        this.editorInput?.nativeElement?.querySelector('[contenteditable="true"]');
      if (el) {
        el.focus();
      }
    }, 0);
  }
}